

// Tour API (Admin)
// Centralized, typed-ish helpers for tour bookings and services
// Uses the shared apiClient for base URL, auth headers, timeouts, retries, and JSON guarding.

import { apiClient } from './apiClient';

const API_BASE =
  apiClient.base ||
  (typeof process !== 'undefined' && process.env && (process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.BASE_URL)) ||
  (typeof import.meta !== 'undefined' && import.meta.env && (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL)) ||
  '';

// -----------------------------
// Types (JSDoc) for intellisense
// -----------------------------
/**
 * @typedef {Object} TourBooking
 * @property {string} _id
 * @property {string} tourId
 * @property {'Waiting for confirmation'|'Confirmed'|'Completed'|'Cancelled'|string} status
 * @property {string} date                 ISO string
 * @property {string=} time                Optional 'HH:mm' or free text
 * @property {string=} transportType
 * @property {boolean=} addSunrise
 * @property {boolean=} addBackTown
 * @property {string=} specialRequest
 * @property {string=} roomNumber
 * @property {number} subtotal
 * @property {number} vat
 * @property {number} total
 * @property {string=} fullName
 * @property {string=} phone
 * @property {string=} email
 * @property {string=} userUid
 * @property {string=} propertyId
 */

/**
 * @typedef {Object} Service
 * @property {string} _id
 * @property {string} title
 * @property {string=} description
 * @property {number=} price
 * @property {string[]=} imageUrls
 * @property {Object=} transportation
 */

// -----------------------------
// Bookings
// -----------------------------

/**
 * List tour bookings (optionally filtered)
 * @param {{propertyId?: string, userUid?: string, status?: string}} opts
 * @returns {Promise<TourBooking[]>}
 */
export function listTourBookings(opts = {}) {
  const query = {
    propertyId: opts.propertyId,
    userUid: opts.userUid,
    status: opts.status,
  };
  if (API_BASE) {
    const qs = Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    const url = `${API_BASE}/api/tour-booking${qs ? `?${qs}` : ''}`;
    return fetch(url, { credentials: 'include' }).then((r) => r.json());
  }
  return apiClient.get('/api/tour-booking', { query });
}

/**
 * Set a booking's status to an explicit value.
 * Backend should accept: { status: 'Confirmed' | 'Completed' | 'Waiting for confirmation' | 'Cancelled' }
 * @param {string} bookingId
 * @param {string} status
 * @returns {Promise<TourBooking>}
 */
export function setTourBookingStatus(bookingId, status) {
  if (API_BASE) {
    return fetch(`${API_BASE}/api/tour-booking/status/${bookingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status }),
    }).then((r) => r.json());
  }
  return apiClient.put(`/api/tour-booking/status/${bookingId}`, { status });
}

/** Convenience wrappers */
export const confirmTourBooking  = (id) => setTourBookingStatus(id, 'Confirmed');
export const completeTourBooking = (id) => setTourBookingStatus(id, 'Completed');
export const waitTourBooking     = (id) => setTourBookingStatus(id, 'Waiting for confirmation');

/**
 * Cancel a booking (uses dedicated cancel endpoint if available)
 * @param {string} bookingId
 * @returns {Promise<TourBooking>}
 */
export function cancelTourBooking(bookingId, reason) {
  if (API_BASE) {
    return fetch(`${API_BASE}/api/tour-booking/cancel/${bookingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    }).then((r) => r.json());
  }
  return apiClient.put(`/api/tour-booking/cancel/${bookingId}`, { reason });
}

// -----------------------------
// Services (tours catalog)
// -----------------------------

/** Fetch a single tour/service by id */
export function getTourById(serviceId) {
  if (API_BASE) {
    return fetch(`${API_BASE}/api/services/${serviceId}`, {
      credentials: 'include',
    }).then((r) => r.json());
  }
  return apiClient.get(`/api/services/${serviceId}`);
}

/** Batch-fetch services by ids (best-effort) */
export async function getToursByIds(ids = []) {
  const uniq = Array.from(new Set(ids.filter(Boolean)));
  const results = await Promise.allSettled(uniq.map((id) => getTourById(id)));
  const map = {};
  results.forEach((r, idx) => {
    const id = uniq[idx];
    if (r.status === 'fulfilled') map[id] = r.value;
  });
  return map;
}

// -----------------------------
// Utilities
// -----------------------------

/** Format money consistently */
export const money = (n = 0) => `$${Number(n || 0).toFixed(2)}`;

// Notes:
// - All requests include auth + base URL + timeouts through apiClient.
// - In dev, you can set VITE_API_URL (or CSV of URLs) and the client will pick a local host automatically.
// - If your backend uses different routes, adjust the paths above.